"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';

const ManutencaoVideogamesPage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };

  // Plataformas - versão compacta
  const plataformas = [
    {
      id: 'playstation',
      title: 'PlayStation',
      description: 'Reparos para todos os modelos, desde o PS1 até o PS5',
      modelos: ['PS1', 'PS2', 'PS3', 'PS4', 'PS5', 'PSP', 'PS Vita']
    },
    {
      id: 'xbox',
      title: 'Xbox',
      description: 'Soluções para todas as gerações de Xbox',
      modelos: ['Xbox Original', 'Xbox 360', 'Xbox One', 'Xbox Series S/X']
    },
    {
      id: 'nintendo',
      title: 'Nintendo',
      description: 'Reparos especializados para a linha Nintendo',
      modelos: ['Switch', '3DS/2DS', 'Wii/Wii U', 'Game Boy', 'SNES', 'N64', 'GameCube']
    },
    {
      id: 'outros',
      title: 'Outros Consoles',
      description: 'Manutenção para diversos outros sistemas',
      modelos: ['Sega', 'Atari', 'Neo Geo', 'Consoles Retrô', 'Steam Deck', 'ROG Ally'],
      observacao: '* Disponibilidade de peças pode ser limitada para consoles antigos.'
    }
  ];

  // Serviços principais - versão compacta
  const servicosPrincipais = [
    {
      titulo: 'Reparo de HDMI',
      descricao: 'Conserto da porta HDMI danificada',
    },
    {
      titulo: 'Limpeza e Manutenção',
      descricao: 'Limpeza interna completa e troca de pasta térmica',
    },
    {
      titulo: 'Reparo de Leitor de Disco',
      descricao: 'Ajuste ou substituição do leitor ótico',
    },
    {
      titulo: 'Reparo de Tela e Controles',
      descricao: 'Conserto de telas quebradas e manutenção de controles',
    },
    {
      titulo: 'Solução para Superaquecimento',
      descricao: 'Correção de problemas de temperatura e desligamentos',
    },
    {
      titulo: 'Atualização de Firmware',
      descricao: 'Atualização de sistema e correção de problemas de software',
    },
    {
      titulo: 'Reparo de Fonte',
      descricao: 'Conserto da fonte do videogame',
    },
    {
      titulo: 'Restauração de Consoles Antigos',
      descricao: 'Recuperação e restauração de consoles retro',
    }
  ];

  // Problemas comuns - versão compacta
  const problemasComuns = [
    {
      titulo: 'Luz vermelha/amarela',
      descricao: 'Superaquecimento ou problema na placa',
      solucao: 'Limpeza, troca de pasta térmica, reparo de componentes'
    },
    {
      titulo: 'Não liga/sem imagem',
      descricao: 'Falha no sistema de energia ou HDMI',
      solucao: 'Diagnóstico eletrônico e reparo de componentes'
    },
    {
      titulo: 'Desliga durante uso',
      descricao: 'Superaquecimento ou fonte com problema',
      solucao: 'Limpeza do sistema de ventilação e verificação da fonte'
    },
    {
      titulo: 'Não reconhece discos',
      descricao: 'Falha no leitor ótico',
      solucao: 'Ajuste ou substituição do conjunto ótico'
    },
    {
      titulo: 'Drift nos controles',
      descricao: 'Desgaste dos analógicos',
      solucao: 'Substituição dos analógicos ou reparo interno'
    }
  ];

  // FAQ items - versão compacta
  const faqItems = [
    {
      pergunta: 'Quanto tempo leva para consertar meu videogame?',
      resposta: 'Reparos simples: 1-3 dias. Problemas complexos: 3-7 dias. Consoles antigos ou peças importadas: até 3 semanas. Fornecemos estimativa após diagnóstico.'
    },
    {
      pergunta: 'Existe garantia para os serviços?',
      resposta: 'Sim. Reparos de hardware: 90 dias. Serviços de software e limpeza: 30 dias.'
    },
    {
      pergunta: 'É possível recuperar um console que caiu ou molhou?',
      resposta: 'Na maioria dos casos, sim. O sucesso depende do tempo entre o incidente e o atendimento.'
    },
    {
      pergunta: 'Vocês fazem upgrade de armazenamento?',
      resposta: 'Sim, realizamos upgrades de HD/SSD com transferência de dados quando possível.'
    },
    {
      pergunta: 'Consertam controles também?',
      resposta: 'Sim, oferecemos reparos completos para controles de todas as marcas.'
    },
    {
      pergunta: 'Vale a pena consertar ou comprar novo?',
      resposta: 'Depende do modelo e problema. Fazemos uma avaliação transparente considerando custo-benefício.'
    }
  ];

  // Benefícios - versão compacta
  const nossosDestaques = [
    {
      titulo: 'Diagnóstico Gratuito',
      descricao: 'Avaliação completa sem compromisso'
    },
    {
      titulo: 'Técnicos Certificados',
      descricao: 'Equipe especializada em consoles'
    },
    {
      titulo: 'Peças Originais',
      descricao: 'Componentes de alta qualidade'
    },
    {
      titulo: 'Garantia em Todos Serviços',
      descricao: 'Sua segurança é nossa prioridade'
    },
    {
      titulo: 'Orçamento Sem Compromisso',
      descricao: 'Transparência em todos os serviços'
    }
  ];

  return (
    <>
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li><Link href="/" className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Início</Link></li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </li>
              <li><Link href="/servicos" className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Serviços</Link></li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </li>
              <li><span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Manutenção de Videogames</span></li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:w-1/2">
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${theme === 'dark' ? 'text-blue-300 bg-blue-900/50' : 'text-blue-800 bg-blue-100'} rounded-full mb-3`}>
                Assistência Técnica Especializada
              </div>
              <h1 className={`text-4xl lg:text-5xl font-bold leading-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                <span className="text-blue-600">Reparo</span> e Manutenção de Videogames
              </h1>
              <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
                Serviço especializado para consoles de todas as gerações. Soluções rápidas e 
                eficientes para que você volte a jogar o quanto antes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a href="#contato" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all text-center"
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: '#ffffff'
                  }}
                >
                  Solicitar Orçamento
                </motion.a>
                <motion.a href="#servicos" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className={`px-8 py-3 ${theme === 'dark' ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'} font-extrabold text-lg rounded-lg shadow hover:shadow-lg transition-all text-center`}>
                  Ver Serviços
                </motion.a>
              </div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.3 }} className="lg:w-1/2">
              <div className="relative">
                <div className={`w-full h-96 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'} flex items-center justify-center overflow-hidden`}>
                  <Image src="/images/manutencao-videogames-illustration.svg" alt="Manutenção de Videogames" width={500} height={400} className="object-contain max-w-full max-h-full" />
                </div>
                
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -right-6 p-6 rounded-lg shadow-xl ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
                  <p className="text-blue-600 font-bold text-4xl">85%</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    taxa de sucesso em reparos
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Plataformas Section */}
      <section id="plataformas" className={`py-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Reparamos Todos os Consoles
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Nossa equipe é especializada no reparo de videogames de todas as gerações, desde consoles retrô até os mais modernos.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plataformas.map((plataforma) => (
              <motion.div 
                key={plataforma.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-750' : 'bg-white hover:bg-blue-50'} shadow-lg hover:shadow-xl transition-all`}
              >
                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>{plataforma.title}</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>{plataforma.description}</p>
                
                <ul className={`mt-4 space-y-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {plataforma.modelos.map((modelo, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {modelo}
                    </li>
                  ))}
                </ul>
                
                {plataforma.observacao && (
                  <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plataforma.observacao}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços Section */}
      <section id="servicos" className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Nossos Serviços
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Oferecemos uma ampla gama de soluções para todos os tipos de problemas em videogames.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicosPrincipais.map((servico, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-50 hover:bg-blue-50'} shadow hover:shadow-md transition-all`}
              >
                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {servico.titulo}
                </h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {servico.descricao}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problemas Comuns Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Problemas Comuns
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Identificamos e resolvemos os problemas mais frequentes em consoles de videogame.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problemasComuns.map((problema, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'} shadow-lg`}
              >
                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
                  {problema.titulo}
                </h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                  {problema.descricao}
                </p>
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                  <p className={`font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>Nossa solução:</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{problema.solucao}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Perguntas Frequentes
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Dúvidas comuns sobre nossos serviços de manutenção de videogames.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto"
               itemScope
               itemType="https://schema.org/FAQPage">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`mb-6 rounded-xl ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                } shadow-md overflow-hidden`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-6 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold" itemProp="name">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div 
                    className={`px-6 pb-6 pt-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    itemScope
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Destaques Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Por Que Escolher Nossos Serviços
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Diferenciais que fazem nossa assistência técnica se destacar.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {nossosDestaques.map((destaque, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl text-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'} shadow-lg`}
              >
                <h3 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {destaque.titulo}
                </h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {destaque.descricao}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Contact Form */}
      <section id="contato" className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Entre em Contato
              </h2>
              <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
                Entre em contato para um diagnóstico gratuito. Nossa equipe técnica está pronta para 
                resolver o problema do seu console com rapidez e eficiência.
              </p>
              
              <div className="space-y-6">
                <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'} 
                  flex items-center justify-center mr-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a href="tel:+551932060706" className={`${theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}>
                      (19) 3206-0706
                    </a>
                  </div>
                </div>

                <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'} 
                  flex items-center justify-center mr-4 ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a href="https://wa.me/5519994799400" target="_blank" rel="noopener noreferrer" 
                    className={`${theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'}`}>
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
              
                <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'} 
                  flex items-center justify-center mr-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a href="mailto:contato@webimports.com.br" className={`${theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}>
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`${theme === 'dark' ? 'bg-gray-700 border border-gray-600' : 'bg-white shadow-xl'} rounded-xl p-6`}
            >
              <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Solicite um diagnóstico gratuito
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ManutencaoVideogamesPage;